/**
 * Transform UI Components - handles, bounding box, and controls container
 */
import * as PIXI from 'pixi.js';
import {
  calculateBoundingBox,
  calculateHandlePositions,
  getInverseScale,
  scaleByViewport
} from '../../utils/transformUtils.js';

// ============================================================================
// HANDLE CREATION AND MANAGEMENT
// ============================================================================

/**
 * Handle types and their visual properties
 */
const HANDLE_TYPES = {
  corner: {
    radius: 6,
    color: 0xC6E982,
    strokeWidth: 4,
    strokeColor: 0x000000
  },
  rotation: {
    radius: 8,
    color: 0xC6E982,
    strokeWidth: 4,
    strokeColor: 0x000000
  }
};

/**
 * Cursor mappings for different handle positions
 */
const HANDLE_CURSORS = {
  // Corner handles (proportional scaling)
  topLeft: 'nw-resize',
  topRight: 'ne-resize',
  bottomLeft: 'sw-resize',
  bottomRight: 'se-resize',

  // Side handles (non-proportional scaling)
  top: 'n-resize',
  right: 'e-resize',
  bottom: 's-resize',
  left: 'w-resize',

  // Rotation handle
  rotation: 'grab'
};

/**
 * Create a transform handle with specified type and position
 */
export const createTransformHandle = (type = 'corner', position = null) => {
  const config = HANDLE_TYPES[type];

  const handle = new PIXI.Graphics();
  handle.circle(0, 0, config.radius);
  handle.fill(config.color);
  handle.stroke({ width: config.strokeWidth, color: config.strokeColor });
  handle.eventMode = 'static';

  // Set cursor based on position
  if (position && HANDLE_CURSORS[position]) {
    handle.cursor = HANDLE_CURSORS[position];
  }

  // Store metadata
  handle.handleType = type;
  handle.handlePosition = position;

  return handle;
};

/**
 * Update handle scale to compensate for viewport zoom
 */
export const updateHandleScale = (handle, inverseScale) => {
  if (handle && handle.scale) {
    handle.scale.set(inverseScale);
  }
};

/**
 * Create all corner handles
 */
export const createCornerHandles = () => ({
  topLeft: createTransformHandle('corner', 'topLeft'),
  topRight: createTransformHandle('corner', 'topRight'),
  bottomLeft: createTransformHandle('corner', 'bottomLeft'),
  bottomRight: createTransformHandle('corner', 'bottomRight')
});

/**
 * Create all side handles
 */
export const createSideHandles = () => ({
  top: createTransformHandle('corner', 'top'),
  right: createTransformHandle('corner', 'right'),
  bottom: createTransformHandle('corner', 'bottom'),
  left: createTransformHandle('corner', 'left')
});

/**
 * Create rotation handle
 */
export const createRotationHandle = () => {
  const handle = createTransformHandle('rotation', 'rotation');

  // Special behavior for rotation handle
  handle.on('pointerup', () => {
    handle.cursor = 'grab';
  });

  return handle;
};

// ============================================================================
// BOUNDING BOX
// ============================================================================

/**
 * Create and manage the transform bounding box
 */
export class TransformBoundingBox {
  constructor(viewport) {
    this.viewport = viewport;
    this.graphics = new PIXI.Graphics();
  }

  /**
   * Update the bounding box for a target
   * @param {PIXI.DisplayObject} target - The target to draw bounding box around
   * @param {Object} options - Options for styling
   * @param {number} options.color - Stroke color (default: 0x666666)
   * @param {number} options.alpha - Alpha transparency (default: 1)
   */
  update(target, options = {}) {
    if (!target || target.destroyed || target.scale === null) return;

    const { color = 0x666666, alpha = 1 } = options;

    this.graphics.clear();
    this.graphics.alpha = alpha;

    const padding = scaleByViewport(0, this.viewport);
    const strokeWidth = scaleByViewport(2, this.viewport);

    const boundingBox = calculateBoundingBox(target, padding);

    // Position the graphics object at the center of the bounding box
    this.graphics.position.set(boundingBox.centerX, boundingBox.centerY);
    this.graphics.rotation = boundingBox.rotation;

    // Draw the rectangle centered at origin (relative to the graphics position)
    this.graphics.rect(
      -boundingBox.width / 2,
      -boundingBox.height / 2,
      boundingBox.width,
      boundingBox.height
    );

    this.graphics.stroke({ width: strokeWidth, color });

    return boundingBox;
  }

  /**
   * Clear the bounding box
   */
  clear() {
    this.graphics.clear();
  }

  /**
   * Get the PIXI graphics object
   */
  getGraphics() {
    return this.graphics;
  }
}

// ============================================================================
// MAIN TRANSFORM CONTROLS
// ============================================================================

/**
 * Main transform controls container that manages all handles and bounding box
 */
export class TransformControls {
  constructor(viewport, target) {
    this.viewport = viewport;
    this.target = target;
    this.container = new PIXI.Container();
    this.container.name = 'transform-controls';

    // Create components
    this.boundingBox = new TransformBoundingBox(viewport);
    this.cornerHandles = createCornerHandles();
    this.sideHandles = createSideHandles();
    this.rotationHandle = createRotationHandle();

    this.setupContainer();
  }

  /**
   * Setup the container with all components
   */
  setupContainer() {
    // Add bounding box to main container
    this.container.addChild(this.boundingBox.getGraphics());

    // Add all handles as children of the bounding box graphics
    // This way they inherit the rotation transformation
    const boundingBoxGraphics = this.boundingBox.getGraphics();

    Object.values(this.cornerHandles).forEach(handle => {
      boundingBoxGraphics.addChild(handle);
    });

    Object.values(this.sideHandles).forEach(handle => {
      boundingBoxGraphics.addChild(handle);
    });

    boundingBoxGraphics.addChild(this.rotationHandle);

    // Store references for easy access
    this.container.boundingBox = this.boundingBox.getGraphics();
    this.container.cornerHandles = this.cornerHandles;
    this.container.sideHandles = this.sideHandles;
    this.container.rotationHandle = this.rotationHandle;
    this.container.target = this.target;
  }

  /**
   * Update all transform controls
   */
  update() {
    if (!this.target || this.target.destroyed || this.target.scale === null) return;

    // Update bounding box and get dimensions
    const boundingBox = this.boundingBox.update(this.target);

    // Calculate handle positions relative to the bounding box center
    // Since handles are now children of the bounding box graphics,
    // positions should be relative to the bounding box center (0,0)
    const relativePositions = this.calculateRelativeHandlePositions(boundingBox);

    // Update corner handle positions (relative to bounding box center)
    Object.entries(this.cornerHandles).forEach(([position, handle]) => {
      const pos = relativePositions.corners[position];
      handle.position.set(pos.x, pos.y);
    });

    // Update side handle positions (relative to bounding box center)
    Object.entries(this.sideHandles).forEach(([position, handle]) => {
      const pos = relativePositions.sides[position];
      handle.position.set(pos.x, pos.y);
    });

    // Update rotation handle position (relative to bounding box center)
    const rotationDistance = scaleByViewport(30, this.viewport);
    this.rotationHandle.position.set(0, -boundingBox.height / 2 - rotationDistance);

    // Update handle scales
    this.updateHandleScales();
  }

  /**
   * Calculate handle positions relative to bounding box center
   */
  calculateRelativeHandlePositions(boundingBox) {
    const halfWidth = boundingBox.width / 2;
    const halfHeight = boundingBox.height / 2;

    return {
      corners: {
        topLeft: { x: -halfWidth, y: -halfHeight },
        topRight: { x: halfWidth, y: -halfHeight },
        bottomLeft: { x: -halfWidth, y: halfHeight },
        bottomRight: { x: halfWidth, y: halfHeight }
      },
      sides: {
        top: { x: 0, y: -halfHeight },
        right: { x: halfWidth, y: 0 },
        bottom: { x: 0, y: halfHeight },
        left: { x: -halfWidth, y: 0 }
      }
    };
  }

  /**
   * Update handle scales to compensate for viewport zoom
   */
  updateHandleScales() {
    const inverseScale = getInverseScale(this.viewport);

    // Update corner handles
    Object.values(this.cornerHandles).forEach(handle => {
      updateHandleScale(handle, inverseScale);
    });

    // Update side handles
    Object.values(this.sideHandles).forEach(handle => {
      updateHandleScale(handle, inverseScale);
    });

    // Update rotation handle
    updateHandleScale(this.rotationHandle, inverseScale);
  }

  /**
   * Add event listeners to handles
   */
  addEventListeners(eventHandlers) {
    // Corner handle events (proportional scaling)
    Object.entries(this.cornerHandles).forEach(([position, handle]) => {
      handle.on('pointerdown', (event) => {
        eventHandlers.onHandleDown(event, 'corner', position, this.container);
      });
    });

    // Side handle events (non-proportional scaling)
    Object.entries(this.sideHandles).forEach(([position, handle]) => {
      handle.on('pointerdown', (event) => {
        eventHandlers.onHandleDown(event, 'side', position, this.container);
      });
    });

    // Rotation handle events
    this.rotationHandle.on('pointerdown', (event) => {
      eventHandlers.onHandleDown(event, 'rotation', 'rotation', this.container);
      this.rotationHandle.cursor = 'grabbing';
    });
  }

  /**
   * Get the container for adding to viewport
   */
  getContainer() {
    return this.container;
  }

  /**
   * Destroy the controls and clean up
   */
  destroy() {
    this.container.destroy();
  }
}